import type { SkaterLook } from "./render/skater-parts";
import { DEFAULT_STATS, type Stats } from "./stats";

// The roster (#64): who you can skate as. Each skater has a look (what the
// model builds) and a starting stat spread. Every spread totals 50, the same
// as the all-5 default, so nobody is strictly better: each trades one
// strength for another. Three.js-free (the look is plain data), so the sim's
// tests and the UI can read it.

export interface SkaterProfile {
  id: string;
  name: string;
  /** One line for the select screen. */
  bio: string;
  /** What they're good at, in a couple of words. */
  style: string;
  look: SkaterLook;
  stats: Readonly<Stats>;
}

/** A spread from the all-5 default with a few stats moved. */
function spread(changes: Partial<Stats>): Readonly<Stats> {
  return Object.freeze({ ...DEFAULT_STATS, ...changes });
}

export const SKATERS: readonly SkaterProfile[] = [
  {
    id: "rex",
    name: "Rex Riot",
    bio: "Pink mohawk, loud shoes, louder grinds. Never met a handrail he didn't like.",
    style: "Street · rails",
    // The owner's rider from #9, unchanged.
    look: {
      skin: "#f2c29b",
      hair: { style: "mohawk", color: "#ff4fb8" },
      top: { style: "tank", color: "#2ec4b6" },
      bottoms: { style: "cutoffs", color: "#5a7fc4" },
      tattoos: [{ arm: "front", ink: "#1f2a6e" }],
      shoes: "#f7f7f2",
      deck: "#ff3d8b",
      wheels: "#ffe14d",
    },
    stats: spread({ rail: 7, hang: 4, landing: 4 }),
  },
  {
    id: "mateo",
    name: "Mateo “Spurs” Delgado",
    bio: "Grew up skating his family's ranch-supply parking lot. Smooth, steady, lands everything.",
    style: "Balance · clean landings",
    look: {
      skin: "#c68a5e",
      hair: { style: "cowboy-hat", color: "#2b1d16", hat: "#e9d7b0" },
      top: { style: "western", color: "#b8323b", trim: "#f4e6c8" },
      bottoms: { style: "pants", color: "#35507f", buckle: "#e0b43c" },
      tattoos: [],
      shoes: "#7a4424",
      shoeStyle: "boots",
      bandana: "#1f4fa0",
      eyes: "#6b4226",
      deck: "#2a9d8f",
      wheels: "#f4a261",
    },
    stats: spread({ landing: 7, lip: 6, manual: 6, air: 4, hang: 4, speed: 4, spin: 4 }),
  },
  {
    id: "nova",
    name: "Nova Okafor",
    bio: "Trained for zero-g, skates like she's still in it. Huge air, not much grip.",
    style: "Big air · hang time",
    look: {
      skin: "#8d5a3b",
      hair: { style: "space-helmet", color: "#1c1413", hat: "#f4f6fb" },
      top: { style: "spacesuit", color: "#f1f3f8", trim: "#1d4ed8" },
      bottoms: { style: "pants", color: "#e7eaf1" },
      tattoos: [],
      shoes: "#eef0f5",
      shoeStyle: "moon",
      eyes: "#4a2a1a",
      build: "narrow",
      deck: "#6c5ce7",
      wheels: "#00d1ff",
    },
    stats: spread({ hang: 8, air: 7, spin: 6, speed: 4, landing: 4, switch: 4, rail: 4, lip: 4, manual: 4 }),
  },
  {
    // The owner's new skater (#227); her hair up in a ponytail and loose pants (#234).
    id: "maddy",
    name: "Maddy",
    bio: "Skates every day after work and never stops smiling. Quick feet, smooth lines, always rolling.",
    style: "Flow · manuals",
    look: {
      skin: "#f3d1bc",
      hair: { style: "ponytail", color: "#6b4226" },
      top: { style: "tshirt", color: "#cc5500" },
      bottoms: { style: "baggy", color: "#1b1b1f" },
      tattoos: [],
      shoes: "#f4f1ea",
      eyes: "#6b4a2e",
      // A soft pink close to her skin (#239), lighter than everyone's
      // default mix (her skin 45% toward red) and the toy grin's plum.
      lips: "#e6b3ab",
      build: "narrow",
      deck: "#264653",
      wheels: "#f4a261",
    },
    stats: spread({ speed: 7, manual: 7, ollie: 6, air: 4, hang: 4, rail: 4, lip: 3 }),
  },
];

export const DEFAULT_SKATER_ID = SKATERS[0]!.id;

export function findSkater(id: string | null | undefined): SkaterProfile {
  return SKATERS.find((s) => s.id === id) ?? SKATERS[0]!;
}

/** The next (or previous, `dir` -1) skater round the roster. */
export function cycleSkater(id: string, dir: 1 | -1): SkaterProfile {
  const i = SKATERS.findIndex((s) => s.id === id);
  return SKATERS[(Math.max(0, i) + dir + SKATERS.length) % SKATERS.length]!;
}

const PICK_KEY = "kickflip.skater";

/** The last skater picked, from storage; the first on the roster when there's none (or no storage). */
export function loadSkaterPick(store: Storage | null): string {
  try {
    return findSkater(store?.getItem(PICK_KEY)).id;
  } catch {
    return DEFAULT_SKATER_ID;
  }
}

export function saveSkaterPick(store: Storage | null, id: string): void {
  try {
    store?.setItem(PICK_KEY, id);
  } catch {
    // Storage full or refused (the artifact frame): the pick lasts the session.
  }
}
